/**
 * Wakes (§10.9): purely event-driven. No self-wakes, dedupe on (agent, reason, subject, subject seq), coalescing into
 * one WakeBatch per turn, debounce for non-human wakes. An agent's @mention wakes as `agent_mention` (Wave A), never
 * as `human_mention`. Chain counts remain visible; only an explicit coordination budget holds further wakes.
 * The inbox below is the Agent DO's state machine, kept pure so it can be simulated.
 */
import { agentOf, itemOf, proposalOf, runOf } from "./state"
import type {
  Actor,
  Agent,
  AgentId,
  Comment,
  EntityRef,
  EventRef,
  Ids,
  Item,
  Monitor,
  Principal,
  ProjectState,
  Run,
  WakeBatch,
  WakeReason,
} from "./types"

/**
 * Wakes of one commit (and of a burst of commits) arrive a few ms apart: a non-human batch waits until the newest
 * queued wake is this old, so they become one turn (decision 0043). Human wakes never wait.
 */
export const WAKE_SETTLE_MS = 3_000
/** The settle window ends at most this long after the oldest queued wake (decision 0043). */
export const WAKE_SETTLE_MAX_MS = 5 * WAKE_SETTLE_MS

export type WakeDecision =
  | { readonly _tag: "deliver"; readonly key: string }
  | {
      readonly _tag: "skip"
      readonly reason: "self_caused" | "duplicate" | "agent_paused" | "agent_retired" | "agent_mention"
    }

/**
 * The subject a reason is about (what the dedupe key and the "why did it wake?" panel point at). Item reasons keep the
 * `tsk:` literal for every item id (decision 0036): a wake caused before the cutover and derived again after it
 * still dedupes against the `minga_wake_seen` rows already stored.
 */
export const wakeSubject = (r: WakeReason): string => {
  switch (r._tag) {
    case "project_work":
      return `req:${r.requestId}`
    case "user_message":
      return `msg:${r.messageId}`
    case "human_mention":
    case "agent_mention":
      return `cmt:${r.commentId}`
    case "assignment":
    case "review_requested":
    case "blocker":
    case "impossible_claimed":
    case "area_request":
    case "triage":
    case "children_closed":
    case "overdue":
    case "spec_changed":
      return `tsk:${r.itemId}`
    case "run_ended":
      return `run:${r.runId}`
    case "ask":
    case "answer":
      return `cmt:${r.askId}`
    case "proposal_resolved":
      return `prop:${r.proposalId}`
    case "kpi_threshold":
      return `kpi:${r.kpiId}`
    case "monitor":
      return `mon:${r.monitorId}`
    // wave C (decision 0071): one daily check per LooseEndsNoted seq (the key's seq); a due series by its item
    case "loose_ends":
      return "chk:daily"
    case "series_due":
      return `tsk:${r.itemId}`
    case "changed":
      return r.subject === "item" ? `tsk:${r.id}` : r.subject === "goal" ? `gol:${r.id}` : `kpi:${r.id}`
  }
}

/** Dedupe key: (agentId, reason, subject, subject.lastSeq). Never a hash of a status. */
export const wakeKey = (agentId: AgentId, reason: WakeReason, subjectSeq: number): string =>
  `${agentId}:${reason._tag}:${wakeSubject(reason)}:${subjectSeq}`

/** Human-caused reasons bypass the debounce. */
export const isHumanWake = (r: WakeReason): boolean => r._tag === "user_message" || r._tag === "human_mention"

/**
 * Decision 0070 (TRI-16): a person's plain note on an item (no @mention) reaches the item's owner as a
 * `changed{edited}` whose note is "note: …", "update: …" or "decision: …" (reactions' `commentNote`).
 */
export const isPersonNote = (r: WakeReason): r is Extract<WakeReason, { _tag: "changed" }> =>
  r._tag === "changed" &&
  r.subject === "item" &&
  r.what === "edited" &&
  r.by._tag === "user" &&
  r.pr === undefined &&
  r.note !== undefined &&
  /^(note|update|decision): /.test(r.note)

/**
 * Whose plain note counts as theirs to ask (decision 0070, widened by 0154): the agent's keeper (`ownerId`), or a
 * predicate over the author. Decision 0154: the Project passes `noteTrustOf(s, keeper)`, so a working project member's
 * note counts like the keeper's; the body (no project state) passes the keeper alone. Absent: nobody's.
 */
export type NoteTrust = Ids["UserId"] | null | undefined | ((userId: Ids["UserId"]) => boolean)

/**
 * Decision 0070: a person's plain note written by the agent's keeper. Decision 0154: or by anyone `trust` accepts (a
 * working member of the project). Everyone else's note (a viewer, someone outside the project) stays a stranger's.
 */
export const isKeeperNote = (r: WakeReason, trust: NoteTrust): boolean => {
  if (trust == null || !isPersonNote(r) || r.by._tag !== "user") return false
  return typeof trust === "function" ? trust(r.by.userId) : r.by.userId === trust
}

/**
 * Decision 0048: a batch is person-started when a person caused one of its wakes (a chat message, a person's mention
 * or comment, or a decision, answer or assignment a person made). Its turn uses the agent's full policy. With
 * `keeper` (the agent's `ownerId`), the keeper's own plain note on the item counts too (decision 0070: a note stays a
 * debounced change, but what the keeper wrote is theirs to ask); anyone else's note stays change-started. Decision
 * 0154: `keeper` may be a predicate (`noteTrustOf`), so a working member's note counts like the keeper's.
 */
export const isPersonStarted = (reasons: ReadonlyArray<WakeReason>, keeper?: NoteTrust): boolean =>
  reasons.some(
    (r) =>
      isHumanWake(r) ||
      ((r._tag === "proposal_resolved" ||
        r._tag === "answer" ||
        r._tag === "assignment" ||
        r._tag === "project_work") &&
        r.byPerson === true) ||
      isKeeperNote(r, keeper),
  )

/**
 * Wave F (decision 0062): who asked for the turn that answers this batch. Attribution only (never a reason to refuse a
 * turn). Passes, first match wins:
 * 0. (wave E) the first `user_message.from` or `human_mention.from`;
 * 1. the first reason that names a person who caused it: `proposal_resolved.by`, `changed.by` when a user, `changed.pr.viaMinga` (`assignment` and `answer` carry only
 *    `byPerson`, so they name nobody);
 * 2. the `askedBy` of the first item a reason names, in batch order (an item without one is skipped): `assignment`,
 *    `run_ended`, `review_requested`, `children_closed`, `spec_changed`, `blocker`, `impossible_claimed`,
 *    `area_request`, `triage`, `overdue`, `changed` on an item. So Lea's follow-up turn on Becky's item is Becky's;
 * 3. the `askedBy` of a `run_ended` run without an item;
 * 4. the agent herself ("Lea, on her own").
 * `lookup` reads state first, then (in the Project DO) the `runs` table for runs compacted out of `ProjectState`.
 *
 * Wave C (decision 0071): a `monitor` wake names the person who set the monitor (`Monitor.ownerId`, through
 * `lookup.monitor`) in pass 1; a `series_due` wake is the series' item in pass 2 (its Asked by); a `loose_ends` wake
 * names nobody (the keeper pays). All three stay change-started (`isPersonStarted` is false for them).
 */
export const askedByOfBatch = (
  reasons: ReadonlyArray<WakeReason>,
  agentId: AgentId,
  lookup: {
    readonly item: (id: Ids["ItemId"]) => Item | null | undefined
    readonly run: (id: Ids["RunId"]) => Run | null | undefined
    /** Wave C: the monitor a `monitor` wake names (state `monitors`). Absent: a monitor wake names nobody. */
    readonly monitor?: (id: Ids["MonitorId"]) => Monitor | null | undefined
    readonly work?: (id: Ids["WorkRequestId"]) => { readonly requester: Ids["UserId"] } | undefined
  },
): Principal => {
  // wave E (decision 0063): a person who wrote in the batch is its asker first (the chat's "answering Becky" agrees)
  for (const r of reasons)
    if (r._tag === "user_message" || r._tag === "human_mention") return { _tag: "user", userId: r.from }
  for (const r of reasons) {
    const person =
      r._tag === "proposal_resolved"
        ? r.by
        : r._tag === "changed"
          ? r.by._tag === "user"
            ? r.by.userId
            : r.pr?.viaMinga
          : r._tag === "monitor"
            ? lookup.monitor?.(r.monitorId)?.ownerId
            : r._tag === "project_work"
              ? lookup.work?.(r.requestId)?.requester
              : undefined
    if (person) return { _tag: "user", userId: person }
  }
  for (const r of reasons) {
    const itemId =
      r._tag === "assignment" ||
      r._tag === "review_requested" ||
      r._tag === "children_closed" ||
      r._tag === "spec_changed" ||
      r._tag === "blocker" ||
      r._tag === "impossible_claimed" ||
      r._tag === "area_request" ||
      r._tag === "triage" ||
      r._tag === "overdue" ||
      r._tag === "series_due"
        ? r.itemId
        : r._tag === "run_ended"
          ? r.itemId
          : r._tag === "changed" && r.subject === "item"
            ? (r.id as Ids["ItemId"])
            : null
    const asked = itemId ? lookup.item(itemId)?.askedBy : undefined
    if (asked) return asked
  }
  for (const r of reasons)
    if (r._tag === "run_ended" && r.itemId === null) {
      const asked = lookup.run(r.runId)?.askedBy
      if (asked) return asked
    }
  return { _tag: "agent", agentId }
}

// #region chain (Wave A §4.0)
/** Wakes that never count (worker reports, review requests, a container closing). */
export const CHAIN_EXEMPT: ReadonlySet<WakeReason["_tag"]> = new Set<WakeReason["_tag"]>([
  "run_ended",
  "review_requested",
  "children_closed",
])

/**
 * A person caused this wake: everything `isPersonStarted` counts, plus a person's change (`changed` by a user: an edit,
 * an approval, an Undo) and a person's decision on a card (`proposal_resolved` with `by`). For the chain only; the
 * policy keeps using `isPersonStarted` (a person's edit still starts a change-started turn, decision 0048).
 */
export const personCaused = (r: WakeReason): boolean =>
  isPersonStarted([r]) ||
  (r._tag === "changed" && r.by._tag === "user") ||
  // decision 0059: a person pressing Merge in Minga resets the chain like an approval (a GitHub-side action does not)
  (r._tag === "changed" && r.pr?.viaMinga !== undefined) ||
  (r._tag === "proposal_resolved" && r.by !== undefined)

/** A reason's `chain` (absent = 0). */
export const chainOfReason = (r: WakeReason): number => ("chain" in r && r.chain !== undefined ? r.chain : 0)

/** The chain of a batch: 0 when a person caused any wake in it (any person's action resets), else the highest `chain`. */
export const batchChain = (reasons: ReadonlyArray<WakeReason>): number =>
  reasons.some(personCaused) ? 0 : reasons.reduce((m, r) => Math.max(m, chainOfReason(r)), 0)

/** The reason without its `chain` (a person's Continue on the chain card delivers it as a fresh start). */
export const stripChain = (r: WakeReason): WakeReason => {
  if (!("chain" in r) || r.chain === undefined) return r
  const { chain: _chain, ...rest } = r
  return rest as WakeReason
}

const HELD_TEXT_MAX = 160
const firstLine = (text: string): string => {
  const line = (text.split("\n").find((l) => l.trim() !== "") ?? "").trim()
  return line.length > HELD_TEXT_MAX ? `${line.slice(0, HELD_TEXT_MAX - 1)}…` : line
}
const actorName = (s: ProjectState, a: Actor): string => {
  switch (a._tag) {
    case "user":
      return s.members.find((m) => m.userId === a.userId)?.displayName ?? "A person"
    case "agent":
      return agentOf(s, a.agentId)?.name ?? "An agent"
    case "worker":
      return "A worker"
    case "runner":
      return "A runner"
    case "system":
      return "Minga"
  }
}
const subjectRef = (s: ProjectState, r: Extract<WakeReason, { _tag: "changed" }>): string =>
  r.subject === "item"
    ? (s.items.find((t) => t.id === r.id)?.ref ?? r.id)
    : r.subject === "goal"
      ? (s.goals.find((g) => g.id === r.id)?.ref ?? r.id)
      : (s.kpis.find((k) => k.id === r.id)?.name ?? r.id)

/** Where a comment is, in words: a record's short ref (DIC-160, G-3, P-4), else its kind. */
const targetRef = (s: ProjectState, t: EntityRef): string => {
  switch (t._tag) {
    case "item":
      return s.items.find((x) => x.id === t.id)?.ref ?? t.id
    case "goal":
      return s.goals.find((x) => x.id === t.id)?.ref ?? t.id
    case "kpi":
      return s.kpis.find((x) => x.id === t.id)?.name ?? t.id
    case "proposal":
      return s.proposals.find((x) => x.id === t.id)?.ref ?? t.id
    case "run":
      return s.runs.find((x) => x.id === t.id)?.ref ?? t.id
    case "agent": {
      const a = agentOf(s, t.id)
      return a ? `@${a.handle}` : t.id
    }
    default:
      return `a ${t._tag}`
  }
}

/** Text an actor wrote may be shown as clean only when it is a person's, the system's, or from a clean run it can be traced to. */
const actorClean = (s: ProjectState, a: Actor): boolean =>
  a._tag === "agent" || a._tag === "worker" ? runOf(s, a.runId)?.taint === "clean" : true

/**
 * The held wake in one line, for the chain card (Wave A §4.0.1): an ask's first line (from `openAsks`), a mention and
 * where it is (`comment`: the comments projection, when the caller has it), a change with its note, else the reason
 * in words. Captured text an agent wrote is quoted, never inlined, and is left out when it comes from a tainted run,
 * an untrusted mention, or a run that can no longer be traced (decision 0040: tainted text is never shown as clean;
 * the card itself is clean).
 */
export const heldLine = (
  s: ProjectState,
  r: WakeReason,
  comment?: (id: Ids["CommentId"]) => Comment | undefined,
): string => {
  const item = (id: string) => s.items.find((t) => t.id === id)?.ref ?? id
  switch (r._tag) {
    case "ask": {
      const c = s.openAsks.find((x) => x.id === r.askId)
      if (!c) return "an ask"
      return c.tainted ? "an ask from a tainted run" : `an ask: "${firstLine(c.body)}"`
    }
    case "answer":
      return "an answer to an ask"
    case "agent_mention": {
      const c = comment?.(r.commentId)
      const where = c ? ` on ${targetRef(s, c.target)}` : ""
      return r.untrusted || c?.tainted === true
        ? `a mention${where} from a tainted run`
        : `a mention${where === "" ? " in a comment" : where}`
    }
    case "changed": {
      const what = `${actorName(s, r.by)} changed ${subjectRef(s, r)}`
      return r.note && actorClean(s, r.by) ? `${what}: "${firstLine(r.note)}"` : what
    }
    case "assignment":
      return `${item(r.itemId)} was assigned`
    case "area_request":
      return `a request for ${item(r.itemId)}`
    case "blocker":
      return `${item(r.itemId)} is blocked`
    case "impossible_claimed":
      return `${item(r.itemId)} was claimed impossible`
    case "proposal_resolved":
      return `a proposal was ${r.state}`
    case "triage":
      return `${item(r.itemId)} entered triage`
    case "overdue":
      return `${item(r.itemId)} is overdue`
    case "spec_changed":
      return `the description of ${item(r.itemId)} changed`
    default:
      return r._tag.replace(/_/g, " ")
  }
}
// #endregion chain

// #region changed
/** A `changed` wake's fields (decision 0048). */
export type ChangedReason = Extract<WakeReason, { _tag: "changed" }>

/** One `changed` per agent and subject: the higher rank wins (lead flow §4.1; Wave A: `approved` ranks like `done`). */
export const CHANGED_RANK: Readonly<Record<ChangedReason["what"], number>> = {
  decided: 4,
  done: 3,
  cancelled: 3,
  approved: 3,
  sent_back: 2,
  edited: 1,
  goal_edited: 1,
  kpi: 1,
  // decision 0059: the PR watch
  merged: 3,
  closed: 3,
  changes_requested: 2,
  ci_failed: 2,
  conflicts: 2,
  new_commits: 2,
  commented: 1,
}

/**
 * Wave A §7: after a pause, several `changed` wakes for one subject become one. The newest wake of the subject stays
 * (its id, key, cause and position); its reason is the highest-ranked one of the group (ties: the newest), so the
 * what, the note and who did it stay together. The others are reported as collapsed into it. A person's change never
 * collapses into another actor's (§4.0: any person's action resets the chain, and the turn must still see that a
 * person did it): people's and others' changes of one subject are grouped apart. Pure; the Agent DO applies it to
 * the batch taken after a resume only.
 */
export const collapseChanged = (
  queued: ReadonlyArray<QueuedWake>,
): {
  readonly keep: ReadonlyArray<QueuedWake>
  readonly collapsed: ReadonlyArray<{ readonly wakeId: Ids["WakeId"]; readonly into: Ids["WakeId"] }>
} => {
  const groups = new Map<string, QueuedWake[]>()
  for (const w of queued) {
    if (w.reason._tag !== "changed") continue
    const k = `${wakeSubject(w.reason)}|${w.reason.by._tag === "user" ? "person" : "other"}`
    const g = groups.get(k)
    if (g) g.push(w)
    else groups.set(k, [w])
  }
  const replace = new Map<Ids["WakeId"], QueuedWake>()
  const drop = new Map<Ids["WakeId"], Ids["WakeId"]>()
  for (const g of groups.values()) {
    if (g.length < 2) continue
    // newest = latest `at`; ties: the later one in the queue
    const newest = g.reduce((n, w) => (w.at >= n.at ? w : n))
    let best = newest
    for (const w of g) {
      const rw = CHANGED_RANK[(w.reason as ChangedReason).what]
      const rb = CHANGED_RANK[(best.reason as ChangedReason).what]
      if (rw > rb || (rw === rb && w.at > best.at)) best = w
    }
    replace.set(newest.wakeId, { ...newest, reason: best.reason })
    for (const w of g) if (w.wakeId !== newest.wakeId) drop.set(w.wakeId, newest.wakeId)
  }
  if (drop.size === 0) return { keep: queued, collapsed: [] }
  return {
    keep: queued.filter((w) => !drop.has(w.wakeId)).map((w) => replace.get(w.wakeId) ?? w),
    collapsed: queued
      .filter((w) => drop.has(w.wakeId))
      .flatMap((w) => {
        const into = drop.get(w.wakeId)
        return into ? [{ wakeId: w.wakeId, into }] : []
      }),
  }
}
// #endregion changed

/**
 * Drop self-caused wakes, a `human_mention` no person caused, wakes for retired agents and duplicates. A paused agent
 * keeps its wakes (Wave A §7): they wait in the inbox for the resume. The `agent_paused` skip reason stays in the type
 * (old logs print it) but is never returned.
 */
export const wakeFilter = (
  agent: Agent,
  reason: WakeReason,
  causeActor: Actor,
  key: string,
  seenKeys: ReadonlySet<string>,
): WakeDecision => {
  if (agent.status === "retired") return { _tag: "skip", reason: "agent_retired" }
  if (causeActor._tag === "agent" && causeActor.agentId === agent.id)
    return { _tag: "skip", reason: "self_caused" }
  if (reason._tag === "human_mention" && causeActor._tag !== "user")
    return { _tag: "skip", reason: "agent_mention" }
  if (seenKeys.has(key)) return { _tag: "skip", reason: "duplicate" }
  return { _tag: "deliver", key }
}

export interface QueuedWake {
  readonly wakeId: Ids["WakeId"]
  readonly reason: WakeReason
  readonly key: string
  readonly cause: EventRef
  readonly at: number
}

/** The Agent DO inbox: queued wakes, whether a turn is live, when the last non-human turn started. */
export interface Inbox {
  readonly queue: ReadonlyArray<QueuedWake>
  readonly seenKeys: ReadonlySet<string>
  readonly turnLive: boolean
  readonly lastAutoTurnAt: number | null
}

export const emptyInbox: Inbox = { queue: [], seenKeys: new Set(), turnLive: false, lastAutoTurnAt: null }

/** Receive one wake: filter, then queue (coalesced behind a live turn). */
export const receiveWake = (
  inbox: Inbox,
  agent: Agent,
  wake: Omit<QueuedWake, "key"> & { readonly subjectSeq: number },
  causeActor: Actor,
): { readonly inbox: Inbox; readonly decision: WakeDecision } => {
  const key = wakeKey(agent.id, wake.reason, wake.subjectSeq)
  const decision = wakeFilter(agent, wake.reason, causeActor, key, inbox.seenKeys)
  if (decision._tag === "skip") return { inbox, decision }
  const seenKeys = new Set(inbox.seenKeys)
  seenKeys.add(key)
  return {
    inbox: {
      ...inbox,
      seenKeys,
      queue: [
        ...inbox.queue,
        { wakeId: wake.wakeId, reason: wake.reason, key, cause: wake.cause, at: wake.at },
      ],
    },
    decision,
  }
}

/** The person who wrote a person wake (`user_message`, `human_mention`), else null. */
export const wakeAuthor = (r: WakeReason): Ids["UserId"] | null =>
  r._tag === "user_message" || r._tag === "human_mention" ? r.from : null

/**
 * Wave E (decision 0063, spec §4.3): whose batch a wake belongs to. A person wake: its author. Another wake: its
 * item's (or run's) Asker, through `askerOf` (`user:<id>` for a person; `own` for the agent's own work). `null` when it
 * can't be told (no `askerOf`): it joins whichever batch is taken.
 */
const batchKeyOf = (w: QueuedWake, askerOf?: WakeAskerOf): string | null => {
  const author = wakeAuthor(w.reason)
  if (author !== null) return `user:${author}`
  if (!askerOf) return null
  const asked = askerOf(w.reason, w)
  if (asked === null) return null
  return asked?._tag === "user" ? `user:${asked.userId}` : "own"
}

/**
 * Whose batch a non-person wake joins: a person (their batch), the agent or `undefined` (its own batch), or `null`
 * (can't be told: it joins whichever batch is taken).
 */
export type WakeAskerOf = (r: WakeReason, w: QueuedWake) => Principal | null | undefined

/**
 * Take the next batch when no turn is live (ten finished items wake the agent once). Non-human batches wait until
 * `lastAutoTurnAt + debounceMs`; a human wake in the queue bypasses it. While the agent is not active there is no batch
 * at all, human wakes included (Wave A §7: a paused agent takes no turn; the Project refuses one anyway). Everything
 * stays queued for the resume.
 *
 * Wave E (decision 0063, spec §4.3): one asker per turn. Person wakes batch per author: the oldest queued person wake's
 * author decides the batch; other authors' messages stay queued, in order (decision 0044 holds per person). Other wakes
 * join a person's batch only when `askerOf` names that person as their asker; otherwise they form their own batch
 * (grouped by asker the same way). Without `askerOf`, other wakes join whichever batch is taken (today's behaviour).
 */
export const nextBatch = (
  inbox: Inbox,
  agent: Agent,
  now: number,
  wakeBatchId: Ids["WakeId"],
  askerOf?: WakeAskerOf,
):
  | {
      readonly _tag: "batch"
      readonly batch: WakeBatch
      readonly wakeIds: ReadonlyArray<Ids["WakeId"]>
      readonly inbox: Inbox
    }
  | { readonly _tag: "wait"; readonly until: number | null } => {
  if (inbox.turnLive || inbox.queue.length === 0) return { _tag: "wait", until: null }
  if (agent.status !== "active") return { _tag: "wait", until: null }
  const keys = inbox.queue.map((w) => batchKeyOf(w, askerOf))
  const firstHuman = inbox.queue.findIndex((w) => isHumanWake(w.reason))
  const human = firstHuman >= 0
  // the batch's key: the oldest person wake's author; else the oldest wake's asker (null = everything)
  const key = human ? keys[firstHuman] : (keys.find((k) => k !== null) ?? null)
  const grouped = inbox.queue.filter((_, i) => key === null || keys[i] === key || keys[i] === null)
  // decision 0070 (TRI-04): a person-started batch carries only person-started reasons; the others stay queued
  const taken = splitPersonStarted(grouped, agent.ownerId).take
  const rest = inbox.queue.filter((w) => !taken.includes(w))
  if (!human) {
    // the debounce since the last automatic turn, and the settle window after the newest wake (decision 0043); the
    // window stops moving WAKE_SETTLE_MAX_MS after the oldest queued wake, so a steady stream can't defer the turn
    const newest = taken.reduce((m, w) => Math.max(m, w.at), 0)
    const oldest = taken.reduce((m, w) => Math.min(m, w.at), Number.POSITIVE_INFINITY)
    const until = Math.max(
      inbox.lastAutoTurnAt !== null ? inbox.lastAutoTurnAt + agent.wake.debounceMs : 0,
      Math.min(newest + WAKE_SETTLE_MS, oldest + WAKE_SETTLE_MAX_MS),
    )
    if (now < until) return { _tag: "wait", until }
  }
  const reasons = taken.map((w) => w.reason) as unknown as WakeBatch["reasons"]
  return {
    _tag: "batch",
    batch: { wakeBatchId, reasons, coalescedFrom: taken.length },
    wakeIds: taken.map((w) => w.wakeId),
    inbox: { ...inbox, queue: rest, turnLive: true, lastAutoTurnAt: human ? inbox.lastAutoTurnAt : now },
  }
}

/**
 * Decision 0070 (TRI-04, amends 0048 "lead flow risk 4"): a batch whose wakes are partly person-started (a chat
 * message, a person's @mention, a decision, answer or assignment a person made) and partly not takes only the
 * person-started ones; the others (a change, a run that ended, an agent's ask) wait for their own change-started turn,
 * in order. So a change never rides on a person's start and gets the agent's full policy. A batch that is all one or
 * the other is taken whole. Exported so the body's chat path applies the same rule.
 */
export const splitPersonStarted = <W extends { readonly reason: WakeReason }>(
  taken: ReadonlyArray<W>,
  keeper?: NoteTrust,
): { readonly take: ReadonlyArray<W>; readonly keep: ReadonlyArray<W> } => {
  const person = taken.filter((w) => isPersonStarted([w.reason], keeper))
  if (person.length === 0 || person.length === taken.length) return { take: taken, keep: [] }
  return { take: person, keep: taken.filter((w) => !person.includes(w)) }
}

/** No agent: `askedByOfBatch`'s fallback, told apart from a real asker. */
const NOBODY = "" as AgentId

/**
 * Wave E (spec §4.3): an `askerOf` for `nextBatch` over project state, with wave F's `askedByOfBatch` lookup for one
 * reason (the item's asker, the run's asker), or undefined when the reason names neither.
 */
export const askerOfReason =
  (lookup: Parameters<typeof askedByOfBatch>[2]) =>
  (r: WakeReason): Principal | undefined => {
    const asked = askedByOfBatch([r], NOBODY, lookup)
    return asked._tag === "agent" && asked.agentId === NOBODY ? undefined : asked
  }

/** Decision 0116: is the card a loose-ends key names still waiting (`card:` on a person, `card_for:` on this agent)? */
const cardKeyOpen = (s: ProjectState, agentId: AgentId, key: string): boolean | null => {
  const m = /^(card|card_for):(.+)$/.exec(key)
  if (!m) return null // not a card key
  const p = proposalOf(s, m[2] as Ids["ProposalId"])
  if (p?.state !== "open") return false
  return m[1] === "card_for" ? p.approver._tag === "agent" && p.approver.agentId === agentId : true
}

/**
 * Decision 0116: a `loose_ends` wake without the cards that were decided or passed on since it was queued (a daily
 * check's `card:<id>`, a chain card's `card_for:<id>`; their lines go with them), or null when nothing is left. Any other
 * reason is returned as it is. The turn file lists only what is still loose.
 */
export const liveLooseEnds = (s: ProjectState, agentId: AgentId, reason: WakeReason): WakeReason | null => {
  if (reason._tag !== "loose_ends") return reason
  const keep = reason.keys.map((k) => cardKeyOpen(s, agentId, k) !== false)
  if (keep.every(Boolean)) return reason
  const keys = reason.keys.filter((_, i) => keep[i])
  if (keys.length === 0) return null
  // lines are index-aligned with keys when the wake was built (reactions, the daily check); otherwise keep them all
  const lines =
    reason.lines.length === reason.keys.length ? reason.lines.filter((_, i) => keep[i]) : reason.lines
  return { ...reason, keys, lines }
}

/**
 * Why a queued wake no longer needs a turn (decision 0043), or null when it does. `lastTurnRequestedSeq` is the seq at
 * which this agent's newest turn that ended `completed` was requested: that turn's state already held any event
 * before it (a turn that failed or never ran does not count). Human wakes, answers to proposals, KPI thresholds and
 * monitors are never stale.
 */
export const staleWake = (
  s: ProjectState,
  agentId: AgentId,
  reason: WakeReason,
  causeSeq: number,
  lastTurnRequestedSeq: number | null,
): string | null => {
  const seenByTurn = lastTurnRequestedSeq !== null && causeSeq > 0 && causeSeq < lastTurnRequestedSeq
  switch (reason._tag) {
    case "project_work": {
      const request = s.workRequests?.find((r) => r.id === reason.requestId)
      if (!request) return "the project request no longer exists"
      if (reason.phase === "request" && !reason.control && request.state !== "pending")
        return "the project request was answered or cancelled"
      if (
        reason.phase === "request" &&
        !reason.control &&
        reason.revision !== undefined &&
        reason.revision !== request.revision
      )
        return "the project request revision changed"
      if (seenByTurn) return "an earlier turn already saw it"
      return null
    }
    case "review_requested": {
      const t = itemOf(s, reason.itemId)
      if (
        t?.status !== "in_review" ||
        t.review.round !== reason.round ||
        t.review.stages[t.review.stage] !== "agent"
      )
        return `${t?.ref ?? reason.itemId} is no longer waiting for the agent review of round ${reason.round}`
      return null
    }
    case "blocker": {
      const t = itemOf(s, reason.itemId)
      if (!t || t.status === "done" || t.status === "cancelled") return `${t?.ref ?? reason.itemId} is closed`
      // a blocker without a run is a cancelled predecessor: the item is todo, waiting on it
      if (reason.runId !== null && t.status !== "blocked") return `${t.ref} is no longer blocked`
      if (seenByTurn) return "an earlier turn already saw it"
      return null
    }
    case "impossible_claimed": {
      const t = itemOf(s, reason.itemId)
      if (!t) return `${reason.itemId} no longer exists`
      // the lead hears that a claim was accepted (the item is cancelled as impossible): not stale
      if (t.status === "cancelled" && t.resolution?._tag === "impossible") return null
      if (t.blocked?._tag !== "impossible_claimed") return `${t.ref} no longer waits on an impossible claim`
      return null
    }
    case "triage": {
      const t = itemOf(s, reason.itemId)
      if (t?.status !== "triage") return `${t?.ref ?? reason.itemId} left triage`
      return null
    }
    case "assignment": {
      const t = itemOf(s, reason.itemId)
      if (!t || t.status === "done" || t.status === "cancelled") return `${t?.ref ?? reason.itemId} is closed`
      return null
    }
    case "run_ended": {
      if (seenByTurn) return "an earlier turn already saw it"
      // decision 0070 (TRI-16): a worker's end folds into what follows it. The review's own wakes and card, or the
      // blocker wake, carry the item; the agent that stopped the run still hears that it ended (decision 0032)
      if (reason.itemId === null) return null
      const run = runOf(s, reason.runId)
      if (run?.cancelRequestedBy?._tag === "agent" && run.cancelRequestedBy.agentId === agentId) return null
      const t = itemOf(s, reason.itemId)
      if (t?.status === "in_review") return `${t.ref} is in review: the review's own wakes follow`
      if (
        t?.status === "blocked" &&
        ((t.blocked?._tag === "reported" && t.blocked.runId !== null) ||
          t.blocked?._tag === "impossible_claimed")
      )
        return `${t.ref} is blocked: the blocker wake carries it`
      return null
    }
    case "changed":
      return seenByTurn ? "an earlier turn already saw it" : null
    // decision 0102: a card wake (`card_for:<id>`) whose cards are all decided or passed on since (a person may
    // decide a card an agent holds)
    // decision 0116: also a daily-check card (`card:<id>`) that was decided since
    case "loose_ends":
      return liveLooseEnds(s, agentId, reason) === null ? "the card was decided or passed on" : null
    default:
      return null
  }
}

/** The turn ended: the next batch may start. On runner_lost the batch's wakes go back to the queue. */
export const turnEnded = (inbox: Inbox, requeue: ReadonlyArray<QueuedWake> = []): Inbox => ({
  ...inbox,
  turnLive: false,
  queue: [...requeue, ...inbox.queue],
})

/**
 * Record the routine that prompted a schedule. Standing delegation governs creation; routine ancestry itself is
 * provenance, not a refusal. Null means a person set it without a wake batch.
 */
export const checkSetMonitor = (
  _kind: Monitor["kind"],
  turnBatch: WakeBatch | null,
):
  | { readonly _tag: "ok"; readonly createdByRoutine: Ids["MonitorId"] | null }
  | { readonly _tag: "rejected"; readonly why: string } => {
  const routine = turnBatch?.reasons.find((r) => r._tag === "monitor" && r.kind === "routine")
  const createdByRoutine = routine?._tag === "monitor" ? routine.monitorId : null
  return { _tag: "ok", createdByRoutine }
}
